import { Controller } from "@hotwired/stimulus";

const ELEMENTS = ["title", "subtitle", "qr", "caption"];
const SNAP_THRESHOLD = 2; // percent: distance to center where snap activates
const CENTER = 50;

export default class extends Controller {
  static targets = [
    "canvas", "layoutInput", "handle",
    "sizeInput", "colorInput", "fontInput", "placeholderInput",
    "weightInput", "italicInput", "alignInput", "enabledInput",
    "lineHeightInput", "letterSpacingInput",
    "placeholderText",
    "verticalGuide", "horizontalGuide",
    "previewToggle",
  ];
  static values = {
    layout: Object,
    pageWidth: Number,
    pageHeight: Number,
    previewMode: { type: Boolean, default: false },
  };

  connect() {
    this.dragState = null;
    this.resizeState = null;
    this.layout = this.normalizedLayout(this.layoutValue);

    this.boundMove = this.onPointerMove.bind(this);
    this.boundEnd = this.onPointerEnd.bind(this);
    this.boundResizeMove = this.onResizeMove.bind(this);
    this.boundResizeEnd = this.onResizeEnd.bind(this);

    this.syncHidden();
  }

  disconnect() {
    document.removeEventListener("pointermove", this.boundMove);
    document.removeEventListener("pointerup", this.boundEnd);
    document.removeEventListener("pointermove", this.boundResizeMove);
    document.removeEventListener("pointerup", this.boundResizeEnd);
  }

  normalizedLayout(input) {
    const layout = input && typeof input === "object" ? { ...input } : {};
    ELEMENTS.forEach((key) => {
      layout[key] = layout[key] || {};
    });
    return layout;
  }

  togglePreview() {
    this.previewModeValue = !this.previewModeValue;
  }

  previewModeValueChanged(value) {
    if (this.hasCanvasTarget) {
      this.canvasTarget.classList.toggle("preview-mode", value);
    }
    if (this.hasPreviewToggleTarget) {
      this.previewToggleTarget.setAttribute("aria-pressed", value ? "true" : "false");
    }
  }

  onPointerDown(event) {
    if (event.target.closest('[data-resize-handle]')) return;

    const handle = event.currentTarget;
    const element = handle.dataset.element;
    if (!element) return;

    event.preventDefault();
    handle.setPointerCapture?.(event.pointerId);

    const rect = this.canvasTarget.getBoundingClientRect();
    const handleRect = handle.getBoundingClientRect();
    this.dragState = {
      element,
      handle,
      offsetX: event.clientX - (handleRect.left + handleRect.width / 2),
      offsetY: event.clientY - (handleRect.top + handleRect.height / 2),
      canvasRect: rect,
    };

    document.addEventListener("pointermove", this.boundMove);
    document.addEventListener("pointerup", this.boundEnd);
  }

  onPointerMove(event) {
    if (!this.dragState) return;
    event.preventDefault();

    const { element, canvasRect, offsetX, offsetY } = this.dragState;

    let x = ((event.clientX - offsetX - canvasRect.left) / canvasRect.width) * 100;
    let y = ((event.clientY - offsetY - canvasRect.top) / canvasRect.height) * 100;

    const snapX = Math.abs(x - CENTER) < SNAP_THRESHOLD;
    const snapY = Math.abs(y - CENTER) < SNAP_THRESHOLD;
    if (snapX) x = CENTER;
    if (snapY) y = CENTER;

    this.toggleGuide("verticalGuide",   snapX);
    this.toggleGuide("horizontalGuide", snapY);

    this.layout[element].x = this.clamp(x);
    this.layout[element].y = this.clamp(y);
    this.renderElement(element);
    this.syncHidden();
  }

  onPointerEnd() {
    if (!this.dragState) return;
    this.dragState = null;
    this.toggleGuide("verticalGuide",   false);
    this.toggleGuide("horizontalGuide", false);
    document.removeEventListener("pointermove", this.boundMove);
    document.removeEventListener("pointerup", this.boundEnd);
  }

  toggleGuide(targetName, show) {
    const hasTarget = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (!this[hasTarget]) return;
    const target = this[`${targetName}Target`];
    target.setAttribute("opacity", show ? "1" : "0");
  }

  onResizeStart(event) {
    event.preventDefault();

    const corner = event.currentTarget;
    const element = corner.dataset.element;
    if (!element) return;
    corner.setPointerCapture?.(event.pointerId);

    const canvasRect = this.canvasTarget.getBoundingClientRect();
    const cfg = this.layout[element] || {};
    this.resizeState = {
      element,
      startX: event.clientX,
      startY: event.clientY,
      startSize:   Number(cfg.size   ?? 0),
      startWidth:  Number(cfg.width  ?? 0),
      startHeight: Number(cfg.height ?? 0),
      startCx:     Number(cfg.x      ?? 50),
      startCy:     Number(cfg.y      ?? 50),
      canvasRect,
    };

    document.addEventListener("pointermove", this.boundResizeMove);
    document.addEventListener("pointerup", this.boundResizeEnd);
  }

  onResizeMove(event) {
    if (!this.resizeState) return;
    event.preventDefault();

    const { element, startX, startY, startSize, startWidth, startHeight,
            startCx, startCy, canvasRect } = this.resizeState;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;

    if (element === "qr") {
      const deltaPct = (dx / canvasRect.width) * 100;
      const newSize = this.clampSize("qr", startSize + deltaPct);
      const actualDelta = newSize - startSize;
      const aspect = canvasRect.width / canvasRect.height;

      this.layout[element].size = this.roundOne(newSize);
      this.layout[element].x = this.roundOne(this.clamp(startCx + actualDelta / 2));
      this.layout[element].y = this.roundOne(this.clamp(startCy + (actualDelta * aspect) / 2));
      this.syncSizeInput(element);
    } else {
      const dWidthPct  = (dx / canvasRect.width)  * 100;
      const dHeightPct = (dy / canvasRect.height) * 100;
      const newWidth  = this.clampPct(startWidth  + dWidthPct,  3, 100);
      const newHeight = this.clampPct(startHeight + dHeightPct, 2, 100);
      const actualDw = newWidth  - startWidth;
      const actualDh = newHeight - startHeight;

      this.layout[element].width  = this.roundOne(newWidth);
      this.layout[element].height = this.roundOne(newHeight);
      this.layout[element].x = this.roundOne(this.clamp(startCx + actualDw / 2));
      this.layout[element].y = this.roundOne(this.clamp(startCy + actualDh / 2));
    }

    this.renderElement(element);
    this.syncHidden();
  }

  roundOne(n) {
    return Math.round(n * 10) / 10;
  }

  clampPct(value, min = 0, max = 100) {
    if (Number.isNaN(value)) return min;
    return Math.max(min, Math.min(max, value));
  }

  onResizeEnd() {
    if (!this.resizeState) return;
    this.resizeState = null;
    document.removeEventListener("pointermove", this.boundResizeMove);
    document.removeEventListener("pointerup", this.boundResizeEnd);
  }

  clampSize(element, value) {
    if (Number.isNaN(value)) return 16;
    if (element === "qr")     return Math.max(5, Math.min(100, value));
    if (element === "caption") return Math.max(4, Math.min(60, value));
    return Math.max(6, Math.min(200, value));
  }

  syncSizeInput(element) {
    const input = this.sizeInputTargets.find((i) => i.dataset.element === element);
    if (input) input.value = this.layout[element].size;
  }

  updateSize(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    const value = parseFloat(event.currentTarget.value);
    if (Number.isNaN(value)) return;
    this.layout[element].size = value;
    this.renderElement(element);
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  updateFont(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    const family = event.currentTarget.value;
    this.layout[element].font = family;
    this.ensureFontLoaded(family);
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  updateWeight(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    this.layout[element].weight = parseInt(event.currentTarget.value, 10) || 400;
    this.ensureFontLoaded(this.layout[element].font);
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  updateItalic(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    this.layout[element].italic = !!event.currentTarget.checked;
    this.ensureFontLoaded(this.layout[element].font);
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  // Toggles whether a title/subtitle slot is rendered at all. Updates both
  // the layout JSON and the visual state of the corresponding handle so the
  // admin sees the change live.
  updateEnabled(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    const enabled = !!event.currentTarget.checked;
    this.layout[element].enabled = enabled;

    this.handleTargets.forEach((handle) => {
      if (handle.dataset.element === element) {
        handle.classList.toggle("card-editor-disabled", !enabled);
      }
    });

    this.syncHidden();
  }

  updateAlign(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    this.layout[element].align = event.currentTarget.value;
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  updateLineHeight(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    const v = parseFloat(event.currentTarget.value);
    if (Number.isNaN(v)) return;
    this.layout[element].line_height = v;
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  updateLetterSpacing(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    const v = parseFloat(event.currentTarget.value);
    if (Number.isNaN(v)) return;
    this.layout[element].letter_spacing = v;
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  updatePlaceholder(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    const value = event.currentTarget.value;
    const key = element === "caption" ? "text" : "placeholder";
    this.layout[element][key] = value;
    const node = this.placeholderTextTargets.find((n) => n.dataset.element === element);
    if (node) node.textContent = value;
    this.syncHidden();
  }

  updateColor(event) {
    const element = event.currentTarget.dataset.element;
    if (!element) return;
    this.layout[element].color = event.currentTarget.value;
    this.applyPreviewStyle(element);
    this.syncHidden();
  }

  applyPreviewStyle(element) {
    const handle = this.handleFor(element);
    if (!handle) return;
    const cfg = this.layout[element] || {};

    if (element !== "qr") {
      handle.style.fontFamily = `'${cfg.font || "Helvetica"}', sans-serif`;
      handle.style.fontWeight = `${cfg.weight || (cfg.bold ? 700 : 400)}`;
      handle.style.fontStyle  = cfg.italic ? "italic" : "normal";
      handle.style.color      = cfg.color || "#000000";
      handle.style.textAlign  = cfg.align || "center";
    }

    const preview = this.placeholderTextTargets.find((n) => n.dataset.element === element);
    if (preview) {
      if (cfg.size)            preview.style.fontSize = `${this.ptToCqw(cfg.size)}cqw`;
      if (cfg.line_height)     preview.style.lineHeight = String(cfg.line_height);
      if (cfg.letter_spacing != null) preview.style.letterSpacing = `${cfg.letter_spacing}em`;
    }
  }

  ptToCqw(sizePt) {
    const pageWidthPt = this.pageWidthValue * 2.83465;
    if (pageWidthPt <= 0) return sizePt;
    return (sizePt / pageWidthPt) * 100;
  }

  ensureFontLoaded(family) {
    if (!family || family === "Helvetica") return;
    const id = `gfont-${family.replace(/\W+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    // Preload every weight the CardTemplate::FontCatalog exposes (300/400/500/
    // 700/900) so switching the weight input never falls back to Regular.
    // Google returns an empty face for weights the family doesn't ship, which
    // is harmless.
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@300;400;500;700;900&display=swap`;
    document.head.appendChild(link);
  }

  renderAll() {
    ELEMENTS.forEach((el) => this.renderElement(el));
  }

  renderElement(element) {
    const handle = this.handleFor(element);
    if (!handle) return;

    const cfg = this.layout[element] || {};
    const x = this.clamp(cfg.x ?? 50);
    const y = this.clamp(cfg.y ?? 50);

    if (element === "qr") {
      const sizeW = this.clampSize("qr", cfg.size ?? 30);
      const canvasW = this.canvasTarget.clientWidth  || 1;
      const canvasH = this.canvasTarget.clientHeight || 1;
      const sizeH  = sizeW * (canvasW / canvasH);
      handle.style.left   = `${x - sizeW / 2}%`;
      handle.style.top    = `${y - sizeH / 2}%`;
      handle.style.width  = `${sizeW}%`;
      handle.style.height = "auto";
      handle.style.aspectRatio = "1 / 1";
      handle.style.transform = "none";
    } else {
      const widthPct  = this.clampPct(cfg.width  ?? 60, 3, 100);
      const heightPct = this.clampPct(cfg.height ?? 8,  2, 100);
      handle.style.left   = `${x - widthPct  / 2}%`;
      handle.style.top    = `${y - heightPct / 2}%`;
      handle.style.width  = `${widthPct}%`;
      handle.style.height = `${heightPct}%`;
      handle.style.aspectRatio = "auto";
      handle.style.transform = "none";
    }
  }

  ptToPreviewPx(sizePt) {
    const canvasHeight = this.canvasTarget.clientHeight || 600;
    const pagePt = this.pageHeightValue * 2.83465;
    if (pagePt <= 0) return sizePt;
    return (sizePt / pagePt) * canvasHeight;
  }

  handleFor(element) {
    return this.handleTargets.find((h) => h.dataset.element === element);
  }

  clamp(value) {
    if (Number.isNaN(value)) return 50;
    return Math.max(0, Math.min(100, value));
  }

  syncHidden() {
    if (!this.hasLayoutInputTarget) return;
    this.layoutInputTarget.value = JSON.stringify(this.layout);
  }
}
